import messageBgImg from '@assets/landing/section6/message_bg_img.png';
import { Box } from '@components/ui/atoms/Box';
import { Container } from '@components/ui/atoms/Container';
import { Flex } from '@components/ui/atoms/Flex';
import { Image } from '@components/ui/atoms/Image';
import { Stack } from '@components/ui/atoms/Stack';
import { Text } from '@components/ui/atoms/Text';
import { fadeUp, floatBox1, floatBox2, floatHover15 } from '@shared/utils/animationsUtil';
import { useScrollReveal } from '@shared/utils/useScrollReveal';
import React from 'react';
import { animated, useSpring } from 'react-spring';

import { ContactSupportForm } from './ContactSupportForm';

function NumberCounter({ n }: { n: number }) {
  const { isVisible: isInView, ref } = useScrollReveal(0.1);

  const { number } = useSpring({
    from: { number: 0 },
    to: { number: isInView ? n : 0 },
    config: { mass: 1, tension: 100, friction: 30 },
    delay: 300,
  });

  return <animated.span ref={ref}>{number.to((val) => val.toFixed(0))}</animated.span>;
}

export const MessageData: React.FC = () => {
  const { isVisible, ref: messageRef } = useScrollReveal(0.1);

  return (
    <Box as="section" w="full" position="relative" overflow="hidden">
      {/* --- Status Text --- */}
      <Box textAlign="center" my="4rem">
        <Text color="white" fontSize="16px" fontWeight="700" mb="2px">
          Industries displayed are representative only.
        </Text>
        <Text color="white" fontSize="16px" fontWeight="700">
          We support businesses across all service and product sectors in Bhutan.
        </Text>
      </Box>
      {/* --- Dark Section with Stats --- */}
      <Box bg="#0B0B0F" pt="1rem" pb="12rem" position="relative" zIndex={2}>
        <Container maxW="container.xl">
          <Flex direction="row" maxW="900px" mx="auto" gap={{ base: '1rem', md: '3rem' }}>
            {/* Column 1 */}
            <Box
              flex="1"
              borderLeft={{ base: '1px solid rgba(255,255,255,0.08)' }}
              pl={{ base: '1rem', md: '3rem' }}
              py="1rem"
            >
              <Box>
                <Text
                  color="#B2B0FE"
                  fontSize={{ base: '22px', md: '40px' }}
                  fontWeight="700"
                  letterSpacing="-0.02em"
                  lineHeight="1.2"
                >
                  <NumberCounter n={475} />
                </Text>
                <Text
                  color="gray.400"
                  fontSize="11px"
                  fontWeight="600"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                  mt="0.5rem"
                >
                  Active Merchants
                </Text>
              </Box>
            </Box>

            {/* Column 2 */}
            <Box
              flex="1"
              borderLeft={{ base: '1px solid rgba(255,255,255,0.08)' }}
              pl={{ base: '1rem', md: '3rem' }}
              py="1rem"
              mt="50px"
            >
              <Stack gap="4rem">
                <Box>
                  <Text
                    color="#B2B0FE"
                    fontSize={{ base: '22px', md: '40px' }}
                    fontWeight="700"
                    letterSpacing="-0.02em"
                    lineHeight="1.2"
                  >
                    $ <NumberCounter n={11} /> M
                  </Text>
                  <Text
                    color="gray.400"
                    fontSize="11px"
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    mt="0.5rem"
                  >
                    Total Invoices
                  </Text>
                </Box>
                <Box>
                  <Text
                    color="#B2B0FE"
                    fontSize={{ base: '22px', md: '40px' }}
                    fontWeight="700"
                    letterSpacing="-0.02em"
                    lineHeight="1.2"
                  >
                    <NumberCounter n={4176} />
                  </Text>
                  <Text
                    color="gray.400"
                    fontSize="11px"
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    mt="0.5rem"
                  >
                    Customers Supported
                  </Text>
                </Box>
              </Stack>
            </Box>

            {/* Column 3 */}
            <Box
              flex="1"
              borderLeft={{ base: '1px solid rgba(255,255,255,0.08)' }}
              pl={{ base: '1rem', md: '3rem' }}
              py="1rem"
            >
              <Stack gap="4rem">
                <Box>
                  <Text
                    color="#B2B0FE"
                    fontSize={{ base: '22px', md: '40px' }}
                    fontWeight="700"
                    letterSpacing="-0.02em"
                    lineHeight="1.2"
                  >
                    <NumberCounter n={7297} />
                  </Text>
                  <Text
                    color="gray.400"
                    fontSize="11px"
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    mt="0.5rem"
                  >
                    Total Payouts
                  </Text>
                </Box>
                <Box>
                  <Text
                    color="#B2B0FE"
                    fontSize={{ base: '22px', md: '40px' }}
                    fontWeight="700"
                    letterSpacing="-0.02em"
                    lineHeight="1.2"
                  >
                    <NumberCounter n={135} />+
                  </Text>
                  <Text
                    color="gray.400"
                    fontSize="11px"
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    mt="0.5rem"
                  >
                    Different Currencies Supported
                  </Text>
                </Box>
              </Stack>
            </Box>
          </Flex>
        </Container>

        {/* Floating Decorative Squares (Overlapping bottom edge) */}
        <Box position="absolute" bottom="-80px" right={{ base: '5%', lg: '15%' }} zIndex={0}>
          {/* Large purple square */}
          <Box
            w="140px"
            h="140px"
            bg="#EDDFFF"
            borderRadius="lg"
            position="absolute"
            right="0"
            bottom="52px"
            zIndex={2}
            animation={`${floatBox1} 6s ease-in-out infinite`}
          />
          {/* Mint square */}
          <Box
            w="80px"
            h="80px"
            bg="#E0FAFA"
            borderRadius="xl"
            position="absolute"
            right="112px"
            bottom="6px"
            animation={`${floatBox2} 6s ease-in-out infinite`}
          />
          {/* Small blue square */}
          <Box
            w="50px"
            h="50px"
            bg="#b7d6faff"
            borderRadius="md"
            position="absolute"
            right="250px"
            bottom="-20px"
            opacity={isVisible ? 1 : 0}
            animation={
              isVisible
                ? `${fadeUp} 0.8s ease-out forwards 0.6s, ${floatHover15} 7s ease-in-out infinite`
                : 'none'
            }
          />
        </Box>
      </Box>

      {/* --- Light Section with Contact Form --- */}
      <Box position="relative" bg="white" minH="700px" pb="4rem" ref={messageRef}>
        {/* Background Image Wrapper */}
        <Box position="absolute" inset="0" zIndex={1} opacity={0.8} overflow="hidden">
          <Image
            src={messageBgImg}
            alt="Abstract building background"
            w="full"
            h="68%"
            objectFit="cover"
            objectPosition="center top"
            opacity={0.6}
            mt="64px"
          />
        </Box>

        <Container
          maxW="container.md"
          position="relative"
          zIndex={10}
          padding={{ base: '1rem', md: '8em' }}
          opacity={isVisible ? 1 : 0}
          animation={isVisible ? `${fadeUp} 0.8s ease-out forwards 0s` : 'none'}
        >
          <ContactSupportForm />
        </Container>
      </Box>
    </Box>
  );
};

MessageData.displayName = 'MessageData';
